import FetchSubjectIndicators from '@Application/Indicators/Queries/FetchSubjectIndicators';
import buildIndicatorScopeKey from '@Application/Indicators/buildIndicatorScopeKey';
import fetchSubjectIndicators from '@Application/Indicators/fetchSubjectIndicators';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import useSubjectTypes from '@UI/Hooks/useSubjectTypes';
import resolveBeneficiaryListScope from '@UI/Pages/Home/Helpers/resolveBeneficiaryListScope';
import useHomeIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope';
import useRetainedIndicatorView from '@UI/Pages/Home/Hooks/useRetainedIndicatorView';
import {
	captionRowStyle,
	captionStyle,
	eyebrowTitleStyle,
	gridStyle,
	sectionStyle,
} from '@UI/Pages/Home/Shared/shortcutCardStyles';
import SubjectTypeShortcutCard from '@UI/Pages/Home/SubjectTypesBlock/SubjectTypeShortcutCard';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

/**
 * One link card per subject type the account serves, with its dossier count for the home scope;
 * renders nothing when there is no type. The period does not enter the count.
 */
const SubjectTypesBlock: React.FC = () => {
	const dispatch = useDispatch();
	const { types } = useSubjectTypes();
	const { status, scope, indicatorScope } = useHomeIndicatorScope();
	const { organizations } = useCurrentOrganization();

	const scopeKey = indicatorScope === null ? null : buildIndicatorScopeKey(indicatorScope);

	const { data, error, refetch } = useStoreQuery({
		selector: (state) => state.indicatorsStore.subjects,
		stopOn: (view) => scopeKey === null || (view !== null && view.queryKey === scopeKey),
		query: () => {
			if (indicatorScope === null) {
				return Promise.resolve();
			}

			return dispatch(fetchSubjectIndicators(new FetchSubjectIndicators({ scope: indicatorScope })));
		},
		deps: [scopeKey],
	});

	const { view } = useRetainedIndicatorView(data, scopeKey);
	const scopeLabel = React.useMemo(
		() => resolveBeneficiaryListScope(scope, organizations).label,
		[scope, organizations],
	);

	if (types === null || types.isEmpty) {
		return null;
	}

	const isAvailable = status !== 'unavailable' && error === null;
	const isLoading = isAvailable && view === null;

	return (
		<section css={sectionStyle}>
			<h2 css={eyebrowTitleStyle}>Dossiers</h2>
			<nav aria-label="Dossiers" css={gridStyle}>
				{types.types.map((type) => (
					<SubjectTypeShortcutCard
						key={type.key}
						type={type}
						count={isAvailable && view !== null ? view.countOf(type.id) : null}
						isLoading={isLoading}
						scopeLabel={scopeLabel}
					/>
				))}
			</nav>
			<ConditionalLoader data={error !== null && status !== 'unavailable'}>
				<div css={captionRowStyle}>
					<p css={captionStyle}>Chiffres indisponibles pour le moment.</p>
					<Button type="button" link label="Réessayer" onClick={refetch} />
				</div>
			</ConditionalLoader>
		</section>
	);
};

export default SubjectTypesBlock;
